import { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { Back } from "../../icon";
import { useLocation, useNavigate } from "react-router-dom";
import { setUploadedImageUrl } from "../../../redux/slices/uploadFlowSlice";
import { useDispatch } from "react-redux";
import { finishHobbyCaricaturePersonCapture } from "../../../utils/hobbyPersonFlow";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  isSnapPrintFlow,
  readSelectedAiSelfieArtFromStorage,
  resolveFifaStationSlug,
  resolveHobbyCaricatureRouteSlug,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import {
  isFifaWorldCup2026Station,
  persistFifaSelfie,
  resolveStationRouteSlug,
} from "../../../utils/stationFlowStorage";
import { store } from "../../../redux/store";
import { initHobbyPeopleSession } from "../../../redux/slices/uploadFlowSlice";
import { uploadTemporaryImage } from "../../../services/fileUploadService";
import { RefreshCcw } from "react-feather";
import RightArrow from "../../icon/RightArrow";
import StationLogo from "../../../assets/img/new-theme/station-logo.png";
import {
  resolveStationThemeBackground,
  resolveStationThemeLogo,
} from "../../../utils/stationTheme";

const TakePhoto = ({
  onClose = () => { },
  onPhotoReady,
  skipBackgroundRemoval = false,
}: {
  onClose?: () => void;
  onPhotoReady?: (imageUrl: string) => void;
  skipBackgroundRemoval?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [cameraPermissionGranted, setCameraPermissionGranted] = useState<
    boolean | null
  >(null);
  const [isRemovingBG, setIsRemovingBG] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isBusy = isRemovingBG || isUploading;
  const location = useLocation();
  const dispatch = useDispatch();
  const themeBackground = resolveStationThemeBackground();
  const logoImage = resolveStationThemeLogo() || StationLogo;
  useEffect(() => {
    if (
      !location.pathname.endsWith("/choose-option") &&
      !location.pathname.endsWith("/choose-portrait") &&
      !location.pathname.endsWith("/select-media-library") &&
      !location.pathname.endsWith("/upload-fifa-image")
    ) return;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 600 },
            height: { ideal: 1000 },
            facingMode: "user",
          },
        });
        setCameraPermissionGranted(true);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play().catch(() => { });
          };
        }
      } catch (error) {
        setCameraPermissionGranted(false);
      }
    };

    startCamera();

    return () => {
      const stream = videoRef.current?.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [location.pathname]);

  const drawVisiblePreviewToCanvas = (
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement
  ) => {
    const context = canvas.getContext("2d");
    if (!context) return false;

    const videoW = video.videoWidth;
    const videoH = video.videoHeight;
    const displayW = video.clientWidth;
    const displayH = video.clientHeight;
    if (!videoW || !videoH || !displayW || !displayH) return false;

    // Match CSS object-fit: cover — crop the camera frame to the visible preview area.
    const videoRatio = videoW / videoH;
    const displayRatio = displayW / displayH;

    let sx = 0;
    let sy = 0;
    let sWidth = videoW;
    let sHeight = videoH;

    if (videoRatio > displayRatio) {
      sWidth = videoH * displayRatio;
      sx = (videoW - sWidth) / 2;
    } else if (videoRatio < displayRatio) {
      sHeight = videoW / displayRatio;
      sy = (videoH - sHeight) / 2;
    }

    canvas.width = Math.max(1, Math.round(sWidth));
    canvas.height = Math.max(1, Math.round(sHeight));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      video,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );
    return true;
  };

  const handleTakePhoto = () => {
    let count = 3;
    setCountdown(count);

    const countdownInterval = setInterval(() => {
      count -= 1;
      if (count === 0) {
        clearInterval(countdownInterval);
        setCountdown(null);

        // Take photo after countdown — same crop as the on-screen preview
        if (!videoRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!drawVisiblePreviewToCanvas(video, canvas)) return;

        const dataUrl = canvas.toDataURL("image/png");
        setImageDataUrl(dataUrl);
      } else {
        setCountdown(count);
      }
    }, 1000);
  };

  const handleRetakePhoto = () => {
    setImageDataUrl(null);
    navigator.mediaDevices
      .getUserMedia({
        video: {
          width: { ideal: 600 },
          height: { ideal: 1000 },
          facingMode: "user",
        },
      })
      .then((stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      });
  };

  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const removeBackground = async (dataUrl: string): Promise<string | null> => {
    setIsRemovingBG(true);
    try {
      const { removeBackgroundWithFreepik } = await import("../../../services/freepikBgRemovalService");
      return await removeBackgroundWithFreepik(dataUrl);
    } catch (err: any) {
      console.log(err.message || "Error removing background");
      return null;
    } finally {
      setIsRemovingBG(false);
    }
  };

  return (
    <section
      className="takePhotoSection stationThemeLayout"
      style={
        themeBackground
          ? {
              backgroundImage: `url("${themeBackground}")`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <div className="stationLogoWrap">
        <img src={logoImage} alt="Station logo" className="stationLogo" />
      </div>
      <Container className="px-0">
        <div className="cameraSection">
          <button
            type="button"
            className="backBtn"
            aria-label="Back"
            onClick={() => onClose()}
          >
            <Back />
          </button>
          {cameraPermissionGranted === false ? (
            <div className="permissionDeniedPhoto">
              <div className="deniedTxt">Camera access denied</div>
              <p>
                Please allow camera access in your browser settings to take a
                photo.
              </p>
              <p>
                - Chrome: Click the lock icon in the address bar → Site settings
                → Camera: Allow
              </p>
              <p>- Safari: Preferences → Websites → Camera → Allow</p>
            </div>
          ) : (
            <>
              {!imageDataUrl ? (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="cameraPlay"
                />
              ) : (
                <img src={imageDataUrl} alt="Captured" className="cameraPlay" />
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />
              {!imageDataUrl ? (
                countdown ? (
                  <div className="countDownTimer">{countdown}</div>
                ) : (
                  <button
                    type="button"
                    className="btnTakePhoto"
                    aria-label="Take a photo"
                    onClick={handleTakePhoto}
                    disabled={cameraPermissionGranted !== true}
                  >
                    <span className="cameraShutterInner" />
                  </button>
                )
              ) : (
                <div className="imageCaptureBTn">
                  <button
                    className="btnTakePhotoRetake"
                    onClick={handleRetakePhoto}
                    disabled={cameraPermissionGranted !== true || isBusy}
                  >
                    Try again <span className="ms-2"><svg width="32" height="32" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.3 15.9492L14.5 21.7492L20.3 27.5492" stroke="#33363F" strokeWidth="2.9" strokeLinecap="round" />
                      <path d="M6.10149 18.4867C4.66512 17.2428 4.0898 15.7966 4.46477 14.3725C4.83974 12.9484 6.14404 11.626 8.17538 10.6103C10.2067 9.59468 12.8516 8.94253 15.6998 8.75504C18.5479 8.56756 21.4402 8.85522 23.9281 9.5734C26.416 10.2916 28.3604 11.4002 29.4598 12.7272C30.5591 14.0542 30.752 15.5256 30.0085 16.913C29.2649 18.3004 27.6266 19.5264 25.3475 20.4008C23.0684 21.2753 20.2759 21.7492 17.4031 21.7492" stroke="#33363F" strokeWidth="2.9" strokeLinecap="round" />
                    </svg>
                    </span>
                  </button>{" "}
                  <button
                    className="btnTakePhotoRetake"
                    onClick={async () => {
                      if (!imageDataUrl) return;

                      let stationDataObj: Record<string, unknown> = {};
                      try {
                        const selectedRaw = localStorage.getItem("selectedStation");
                        const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
                        const fromStorage = JSON.parse(
                          localStorage.getItem("stationData") || "{}"
                        );
                        stationDataObj = fromSelected || fromStorage;
                      } catch {
                        stationDataObj = {};
                      }

                      const stationType = stationDataObj?.type as string | undefined;
                      const selectedArt = readSelectedAiSelfieArtFromStorage();
                      const artSlug = resolveStationRouteSlug(
                        stationDataObj as any,
                        undefined,
                        selectedArt
                      );
                      const isSnapPrint = isSnapPrintFlow({
                        slug: artSlug,
                        stationType,
                        artName: selectedArt?.name,
                      });
                      // Prefer prop from choose-option so we never call BG API for Print Your Own.
                      const uploadOnly = skipBackgroundRemoval || isSnapPrint;

                      // Group-photo extraction needs the original frame (not cut-out).
                      let finalImage = imageDataUrl;
                      if (!uploadOnly) {
                        const bgRemovedImage = await removeBackground(imageDataUrl);
                        if (!bgRemovedImage) return;
                        finalImage = bgRemovedImage;
                      } else {
                        // Print Your Own: upload only — never call remove-bg on Proceed.
                        setIsUploading(true);
                        try {
                          const uploaded = await uploadTemporaryImage(finalImage);
                          if (!uploaded.success || !uploaded.url) {
                            console.log(uploaded.error || "Upload failed");
                            return;
                          }
                          finalImage = uploaded.url;
                        } finally {
                          setIsUploading(false);
                        }
                      }

                      const isHobby =
                        stationType === "hobby-caricature" ||
                        isAiSelfieCaricatureHobbyPostPaymentActive() ||
                        shouldUseHobbyCaricatureFlowForStoredAiSelfieArt();
                      const isFifa = isFifaWorldCup2026Station(
                        stationDataObj,
                        selectedArt
                      );

                      if (onPhotoReady) {
                        dispatch(setUploadedImageUrl(finalImage));
                        onPhotoReady(finalImage);
                        onClose();
                        return;
                      }

                      dispatch(setUploadedImageUrl(finalImage));

                      if (isHobby) {
                        const { hobbyPeopleCount } = store.getState().uploadFlow;
                        if (hobbyPeopleCount == null) {
                          dispatch(initHobbyPeopleSession(1));
                        }
                        const hobbySlug = resolveHobbyCaricatureRouteSlug(
                          resolveStationRouteSlug(stationDataObj, undefined, selectedArt)
                        );
                        finishHobbyCaricaturePersonCapture(
                          navigate,
                          hobbySlug,
                          finalImage,
                          { onCloseModal: onClose }
                        );
                        return;
                      }

                      if (isFifa) {
                        persistFifaSelfie(finalImage);
                        const fifaSlug = resolveFifaStationSlug(
                          stationDataObj,
                          selectedArt
                        );
                        onClose();
                        navigate(`/${fifaSlug}/upload-fifa-image`, {
                          state: { capturedImage: finalImage },
                        });
                        return;
                      }

                      // Both `is_caricature_hobby` and `is_fifa` false → standard transform.
                      if (artSlug) {
                        navigate(`/${artSlug}/image-transformation`, {
                          state: {
                            capturedImage: finalImage,
                            stationData: stationDataObj,
                          },
                        });
                      } else {
                        navigate("/select-filter", {
                          state: { capturedImage: finalImage },
                        });
                      }
                    }}
                    disabled={cameraPermissionGranted !== true || isBusy}
                  >
                    Proceed <span className="ms-2"><RightArrow /></span>
                  </button>
                </div>

              )}
            </>
          )}
        </div>
      </Container>
      {isBusy && (
        <div className="bg-removal-loader">
          <div className="bg-removal-loader-card">
            <div className="loader-spinner"></div>
            <span>{isUploading ? "Uploading..." : "Removing background..."}</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default TakePhoto;
